import React, { useCallback, useContext, useMemo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import useUnbookReasons from '@ubeya/shared/hooks/account/useUnbookReasons';
import Select from '../Select/Select';
import ConfigContext from '../../contexts/ConfigContext';
import { FlexColumn, FlexMiddle } from '../Flex';
import { Small, SmallBody } from '../Typography/Typography';
import { ReactComponent as BaseArrowIcon } from '../../assets/arrow-right-small.svg';

const ArrowIcon = styled(BaseArrowIcon)`
  transform: rotate(90deg);
`;

/* The menu is portalled, so it cannot be reached from the wrapper below. Options are wrapped in a
   shrink-to-fit element by Select; the pills only reach the row's right edge once it fills.
   This mounts once per select, so a table of them registers the rule per row - Select exposing the
   fill would remove the need for it. */
const MenuStyles = createGlobalStyle`
  .unbook-reason-menu .text-with-tooltip-wrapper {
    flex: 1;
  }
`;

const Wrapper = styled(FlexColumn).attrs({ className: 'unbook-reason-select-wrapper' })`
  /* Hosts place this in a flex cell, where width alone leaves the field sized to its content. */
  flex: 1;
  width: 100%;
  gap: 4px;

  .select {
    width: 100%;
  }
`;

const OptionalHint = styled.span`
  margin-left: 4px;
`;

const OptionContent = styled(FlexColumn)`
  gap: 2px;
  min-width: 0;
`;

const OptionName = styled(FlexMiddle)`
  gap: 8px;
`;

/* The pill cannot shrink, so the name is what gives way - otherwise a long one pushes the pill
   past the row's clipping edge and it is cut mid-word. */
const OptionLabel = styled(SmallBody)`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OptionDescription = styled(Small)`
  color: ${({ theme }) => theme.colors.grey4};
  white-space: normal;
`;

const NoShowPill = styled(Small)`
  background: ${({ theme }) => theme.colors.redExtraLight};
  color: ${({ theme }) => theme.colors.redNegative};
  border-radius: 12px;
  padding: 2px 8px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const SelectedDescription = styled(Small)`
  color: ${({ theme }) => theme.colors.grey3};
`;

const UnbookReasonSelect = ({
  value,
  onChange,
  title,
  optional = false,
  showSelectedDescription = true,
  error,
  clearable = true,
  allowNoShowReasons = true,
  isSaving = false
}) => {
  const { t } = useContext(ConfigContext);
  const { unbookReasonsOptions, unbookReasonsMap, isLoading } = useUnbookReasons();

  const selectableReasons = useMemo(
    () =>
      allowNoShowReasons ? unbookReasonsOptions : unbookReasonsOptions.filter(({ countAsNoShow }) => !countAsNoShow),
    [allowNoShowReasons, unbookReasonsOptions]
  );

  const options = useMemo(
    () =>
      selectableReasons.map(({ value: optionValue, label, description, countAsNoShow }) => ({
        value: optionValue,
        displayValue: label,
        label: (
          <OptionContent>
            <OptionName>
              <OptionLabel>{label}</OptionLabel>
              {countAsNoShow && <NoShowPill>{t('countsAsNoShow')}</NoShowPill>}
            </OptionName>
            {!!description && <OptionDescription>{description}</OptionDescription>}
          </OptionContent>
        )
      })),
    [selectableReasons, t]
  );

  // Value.js renders the placeholder unless the selected id is found in options or here, so a
  // deleted reason - or one hidden by allowNoShowReasons - would read as empty without this.
  const historicalOptions = useMemo(() => Object.values(unbookReasonsMap), [unbookReasonsMap]);

  const handleChange = useCallback((unbookReasonId) => onChange(unbookReasonId ? Number(unbookReasonId) : null), [
    onChange
  ]);

  const formatValue = useCallback(({ value: selectedValue }) => unbookReasonsMap[selectedValue]?.label, [
    unbookReasonsMap
  ]);

  // An option's label is the node below, and Select's default filter lowercases the label as it is.
  // Matches the name instead - what the closed field shows, and what displayValue carries.
  const filterByName = useCallback(
    ({ displayValue }, search) => (displayValue || '').toLowerCase().includes((search || '').toLowerCase()),
    []
  );

  const selectTitle = useMemo(
    () =>
      title ? (
        <>
          {title}
          {optional && <OptionalHint>({t('optional')})</OptionalHint>}
        </>
      ) : undefined,
    [optional, t, title]
  );

  const selectedDescription = unbookReasonsMap[value]?.description;

  // While the reasons are still loading the list is legitimately empty - keep the field (and its
  // spinner) mounted so it does not pop in mid-prompt.
  if (!isLoading && options.length === 0 && !value) {
    return null;
  }

  return (
    <Wrapper>
      <MenuStyles />
      <Select
        title={selectTitle}
        options={options}
        displayInitialValueNotInOptions={historicalOptions}
        value={value}
        error={error}
        onChange={handleChange}
        formatValue={formatValue}
        filterFunc={filterByName}
        icon={ArrowIcon}
        placeholder={t('selectAReason')}
        isLoading={isLoading || isSaving}
        menuClassName="unbook-reason-menu"
        optionsFullWidth
        useMinWidthAsMaxWidth
        usePortal
        clearable={clearable}
      />
      {showSelectedDescription && !!selectedDescription && (
        <SelectedDescription>{selectedDescription}</SelectedDescription>
      )}
    </Wrapper>
  );
};

export default UnbookReasonSelect;
